import { ExternalLink, Link as LinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { detectNetwork, explorerUrl, isValidTxHash, networkLabel, type Network } from '../../lib/network';

interface TxHashFieldProps {
  label?: string;
  walletAddress?: string | null;
  walletType?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function TxHashField({
  label,
  walletAddress,
  walletType,
  value,
  onChange,
  disabled,
  compact,
}: TxHashFieldProps) {
  const { t } = useTranslation();
  if (walletType && walletType !== 'crypto') return null;

  const network: Network = detectNetwork(walletAddress);
  if (network === 'unknown') return null;

  const valid = isValidTxHash(value);
  const url = value && valid ? explorerUrl(network, value) : null;
  const netLabel = networkLabel(network);

  const fieldLabel = label ?? t('cashier.txHash.label', 'TX Hash');

  return (
    <div className={`form-group ${compact ? '' : ''}`}>
      <label className="form-label flex items-center gap-1">
        <LinkIcon size={12} />
        {fieldLabel}
        <span className="text-muted" style={{ fontWeight: 400, marginLeft: 4 }}>
          ({netLabel})
        </span>
      </label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          className={`form-input-text ${!valid ? 'input-error' : ''}`}
          placeholder={
            network === 'ethereum'
              ? '0x…'
              : t('cashier.txHash.placeholder', 'on-chain transaction hash')
          }
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          spellCheck={false}
          autoComplete="off"
          style={{ fontFamily: 'monospace', fontSize: '0.85em', flex: 1 }}
        />
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
            title={t('cashier.txHash.openExplorer', 'Open in explorer')}
            style={{ padding: '0.25rem 0.5rem' }}
          >
            <ExternalLink size={14} />
          </a>
        )}
      </div>
      {!valid && (
        <span className="field-error">
          {t('cashier.txHash.invalid', 'Invalid transaction hash')}
        </span>
      )}
    </div>
  );
}
